import React, { useRef, useState, useEffect } from 'react';
import Slider from 'react-slick';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuoteLeft, faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';
// Removed API_BASE_URL, API_ENDPOINTS as they are no longer needed here
import { useToast } from '../../../contexts/ToastContext';
import { fetchTestimonials } from '../../../api/testimonialApi'; // Import fetchTestimonials

function TestimonialsSlider() {
  const sliderRef = useRef(null);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { addToast } = useToast();

  useEffect(() => {
    const getTestimonialsData = async () => { // Renamed to avoid conflict with imported function
      try {
        setLoading(true);
        // Use the imported fetchTestimonials function
        const data = await fetchTestimonials({ limit: 3, paginate: false });

        if (data.success) {
          const formattedTestimonials = data.data.map(t => ({
            id: t.id,
            quote: t.content,
            author: t.name,
            title: t.client_designation,
            image: t.image,
          }));
          setTestimonials(formattedTestimonials);
        } else {
          addToast('error', data.message || 'Failed to fetch testimonials.');
        }
      } catch (e) {
        console.error("Error fetching testimonials:", e);
        addToast('error', 'Failed to load testimonials. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    getTestimonialsData(); // Call the renamed function
  }, [addToast]);

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    arrows: false,
    beforeChange: (oldIndex, newIndex) => setCurrentSlide(newIndex),
  };

  // If no testimonials and not loading, return null to hide the section
  if (!loading && testimonials.length === 0) {
    return null;
  }

  return (
    <section className="py-16 bg-bg-dark text-text-gray border-y border-border-subtle">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl font-bold text-center text-text-white relative after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-20 after:h-1 after:bg-accent-red after:rounded-sm after:bottom-[-15px] mb-12">Words From My Clients</h2>

        {loading ? (
          <div className="flex flex-col md:flex-row justify-center items-center max-w-6xl mx-auto">
            <div className="w-full relative mb-8 md:mb-0 md:pr-4">
              <div className="bg-white/5 p-8 rounded-lg shadow-lg flex flex-col sm:flex-row items-center sm:items-center space-y-4 sm:space-y-0 sm:space-x-6 max-w-none w-full mx-auto animate-pulse">
                <div className="flex-shrink-0">
                  <div className="w-32 h-32 rounded-full bg-gray-700 border-2 border-gray-600"></div>
                </div>
                <div className="text-center sm:text-left w-full">
                  <div className="h-8 bg-gray-700 rounded w-1/4 mb-4 mx-auto sm:mx-0"></div>
                  <div className="h-6 bg-gray-700 rounded w-full mb-2"></div>
                  <div className="h-6 bg-gray-700 rounded w-5/6 mb-6 mx-auto sm:mx-0"></div>
                  <div className="h-4 bg-gray-700 rounded w-1/2 mb-1 mx-auto sm:mx-0"></div>
                  <div className="h-4 bg-gray-700 rounded w-1/3 mx-auto sm:mx-0"></div>
                </div>
              </div>
            </div>
            <div className="w-full md:w-1/12 flex flex-row md:flex-col items-center justify-center space-x-4 md:space-x-0 md:space-y-4 md:pl-4">
              <div className="p-2 rounded-full bg-gray-700 w-10 h-10"></div>
              <ul className="flex flex-row md:flex-col space-x-2 md:space-x-0 md:space-y-2">
                <li className="w-3 h-3 rounded-full bg-gray-700"></li>
                <li className="w-3 h-3 rounded-full bg-gray-700"></li>
                <li className="w-3 h-3 rounded-full bg-gray-700"></li>
              </ul>
              <div className="p-2 rounded-full bg-gray-700 w-10 h-10"></div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col md:flex-row justify-center items-center max-w-6xl mx-auto">
            <div className="w-full md:w-11/12 relative mb-8 md:mb-0 md:pr-4">
              <Slider {...settings} ref={sliderRef}>
                {testimonials.map(testimonial => (
                  <div key={testimonial.id} className="px-2">
                    <div className="bg-white/5 p-8 rounded-lg shadow-lg flex flex-col sm:flex-row items-center sm:items-center space-y-4 sm:space-y-0 sm:space-x-6 max-w-none w-full mx-auto">
                      <div className="flex-shrink-0">
                        <img
                          src={testimonial.image}
                          alt={testimonial.author}
                          className="w-32 h-32 rounded-full object-cover border-2 border-accent-red"
                        />
                      </div>
                      <div className="text-center sm:text-left">
                        <FontAwesomeIcon icon={faQuoteLeft} className="text-accent-red text-3xl mb-4" />
                        <p className="text-lg italic mb-6 text-text-gray">"{testimonial.quote}"</p>
                        <p className="font-semibold text-text-white">{testimonial.author}</p>
                        <p className="text-sm text-text-gray">{testimonial.title}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </Slider>
            </div>

            <div className="w-full md:w-1/12 flex flex-row md:flex-col items-center justify-center space-x-4 md:space-x-0 md:space-y-4 md:pl-4">
              <button
                className="p-2 rounded-full bg-accent-red hover:bg-red-700 focus:outline-none"
                onClick={() => sliderRef.current.slickPrev()}
                aria-label="Previous Testimonial"
              >
                <FontAwesomeIcon icon={faArrowUp} className="text-white text-xl" />
              </button>

              <ul className="flex flex-row md:flex-col space-x-2 md:space-x-0 md:space-y-2">
                {testimonials.map((_, index) => (
                  <li key={index}>
                    <button
                      onClick={() => sliderRef.current.slickGoTo(index)}
                      className={`w-3 h-3 rounded-full focus:outline-none ${
                        currentSlide === index
                          ? 'bg-accent-red'
                          : 'bg-text-gray/50 hover:bg-text-gray'
                      }`}
                      aria-label={`Go to slide ${index + 1}`}
                    ></button>
                  </li>
                ))}
              </ul>

              <button
                className="p-2 rounded-full bg-accent-red hover:bg-red-700 focus:outline-none"
                onClick={() => sliderRef.current.slickNext()}
                aria-label="Next Testimonial"
              >
                <FontAwesomeIcon icon={faArrowDown} className="text-white text-xl" />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default TestimonialsSlider;